/**
 * Central timezone utilities for consistent time handling across the app
 * This ensures all times are saved and displayed correctly regardless of timezone
 */

/**
 * Converts a date string (YYYY-MM-DD) and time string (HH:MM AM/PM) to local ISO string
 * This ensures the time is saved as the user intended in their local timezone
 */
export const createUTCDateTime = (dateString: string, timeString: string): string => {
  // Parse the date components
  const [year, month, day] = dateString.split('-').map(Number);
  
  // Convert time to 24-hour format
  const time24 = convertTimeTo24Hour(timeString);
  const [hour, minute] = time24.split(':').map(Number);
  
  // Create date in local timezone (not UTC) to preserve user's intended time
  const localDate = new Date(year, month - 1, day, hour, minute, 0, 0);
  
  return localDate.toISOString();
};

/**
 * Converts a UTC ISO string back to local time for display
 * This ensures times are displayed in the user's local timezone
 */
export const formatDateTimeForDisplay = (utcIsoString: string): { date: string; time: string } => {
  const date = new Date(utcIsoString);
  
  // Format date as YYYY-MM-DD
  const dateString = date.getFullYear() + '-' + 
    String(date.getMonth() + 1).padStart(2, '0') + '-' + 
    String(date.getDate()).padStart(2, '0');
  
  // Format time as 12-hour format
  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  
  return { date: dateString, time: timeString };
};

/**
 * Converts 12-hour time format to 24-hour format
 * Used internally by createUTCDateTime
 */
function convertTimeTo24Hour(time12: string): string {
  const [time, period] = time12.split(' ');
  let [hours, minutes] = time.split(':').map(Number);
  
  if (period === 'AM' && hours === 12) {
    hours = 0;
  } else if (period === 'PM' && hours !== 12) {
    hours += 12;
  }
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Gets the current date in YYYY-MM-DD format (local timezone)
 * Used for initializing date inputs
 */
export const getCurrentDateString = (): string => {
  const now = new Date();
  return now.getFullYear() + '-' + 
    String(now.getMonth() + 1).padStart(2, '0') + '-' + 
    String(now.getDate()).padStart(2, '0');
};

/**
 * Validates if a date/time combination is in the past
 * Used for preventing scheduling classes in the past
 */
export const isDateTimeInPast = (dateString: string, timeString: string): boolean => {
  const utcDateTime = createUTCDateTime(dateString, timeString);
  const now = new Date();
  return new Date(utcDateTime) < now;
};
